const DEFAULT_FIELDS = ['hasService', 'productLine', 'index']

const FIELD_DESCRIPTIONS = {
  hasService: 'Whether this product belongs to this service. YES if present, leave blank if absent. Editable, not required on create.',
  productLine: 'Which product line, if any, does this product belong to at this service? Editable, not required on create.',
  index: 'If applicable, what is this product’s index within its product line? Editable, not required on create. Number only.'
}

const serviceIdToColumnBase = serviceId =>
  serviceId.replace('program:', 'programs.').replace(':service:', '.services.')

const byServiceIdAsc = (a, b) => a.localeCompare(b)

const isWarehouseService = serviceId => serviceIdToColumnBase(serviceId).includes('.warehouse')

const normalizeServiceIds = (serviceIds = [], { excludeWarehouse = true, sort = 'serviceIdAsc' } = {}) => {
  const filtered = Array.from(new Set(serviceIds))
    .filter(Boolean)
    .filter(serviceId => !excludeWarehouse || !isWarehouseService(serviceId))

  if (sort === 'serviceIdAsc') {
    return filtered.sort(byServiceIdAsc)
  }

  if (typeof sort === 'function') {
    return filtered.sort(sort)
  }

  return filtered
}

const getServiceIdsFromConfigurationDocs = (configurationDocs = [], opts = {}) => {
  const serviceIds = configurationDocs.map(doc => doc.service)
  return normalizeServiceIds(serviceIds, opts)
}

const getServiceFromProduct = (product, serviceId) => {
  const services = product && Array.isArray(product.services) ? product.services : []
  return services.find(service => service.id === serviceId)
}

const getHasServiceValue = service => (service ? 'YES' : '')

const getFieldValue = (service, field) => {
  if (!service) return ''
  return service[field] === undefined ? '' : service[field]
}

const buildServiceColumns = (serviceIds = [], {
  excludeWarehouse = true,
  sort = 'serviceIdAsc',
  fields = DEFAULT_FIELDS
} = {}) => {
  const normalizedServiceIds = normalizeServiceIds(serviceIds, { excludeWarehouse, sort })
  const additionalColumns = {}

  for (const serviceId of normalizedServiceIds) {
    const servicePath = serviceIdToColumnBase(serviceId)

    for (const field of fields) {
      const key = `${servicePath}.${field}`

      additionalColumns[key] = {
        title: key,
        description: FIELD_DESCRIPTIONS[field] || '',
        transform: product => {
          const service = getServiceFromProduct(product, serviceId)
          if (field === 'hasService') {
            return getHasServiceValue(service)
          }
          return getFieldValue(service, field)
        }
      }
    }
  }

  return additionalColumns
}

const programIdFromColumnPath = columnPath => columnPath.split('.')[1]

const warehouseColumnTitle = programId => `Is the product in the ${programId} warehouse service?`

const WAREHOUSE_COLUMN_TITLE_PATTERN = /^Is the product in the .+ warehouse service\?$/

// One column per program's own warehouse service (YES when present, blank
// when absent - same no-action-on-blank convention as hasService above).
//
// Unlike ordinary services, warehouse membership isn't denormalized onto
// product.services - it only lives in the warehouse configuration doc's own
// `products` list, so membership is checked there instead of via
// getServiceFromProduct.
const buildWarehouseServiceColumns = (configurationDocs = []) => {
  const warehouseConfigurationDocs = configurationDocs
    .filter(doc => doc && doc.service && isWarehouseService(doc.service))

  const columnsByProgram = {}
  for (const doc of warehouseConfigurationDocs) {
    const key = serviceIdToColumnBase(doc.service)
    const programId = programIdFromColumnPath(key)
    const productIds = new Set(doc.products || [])

    columnsByProgram[programId] = {
      key,
      column: {
        title: warehouseColumnTitle(programId),
        description: `Whether this product is on the ${programId} warehouse service. YES if present, leave blank if absent. Editable, not required on create.`,
        transform: product => (productIds.has(product._id) ? 'YES' : '')
      }
    }
  }

  return columnsByProgram
}

// Places each program's warehouse column right before that program's own
// first service column.
const injectWarehouseColumns = (serviceColumns, warehouseColumnsByProgram) => {
  const result = {}
  const seenPrograms = new Set()

  for (const key of Object.keys(serviceColumns)) {
    const programId = programIdFromColumnPath(key)
    if (!seenPrograms.has(programId)) {
      seenPrograms.add(programId)
      const warehouseColumn = warehouseColumnsByProgram[programId]
      if (warehouseColumn) {
        result[warehouseColumn.key] = warehouseColumn.column
      }
    }
    result[key] = serviceColumns[key]
  }

  // A program whose only configured service is its warehouse service never
  // shows up above - still include its column, appended at the end.
  for (const programId of Object.keys(warehouseColumnsByProgram)) {
    if (seenPrograms.has(programId)) continue
    const { key, column } = warehouseColumnsByProgram[programId]
    result[key] = column
  }

  return result
}

module.exports = {
  DEFAULT_FIELDS,
  serviceIdToColumnBase,
  getServiceIdsFromConfigurationDocs,
  buildServiceColumns,
  buildWarehouseServiceColumns,
  injectWarehouseColumns,
  WAREHOUSE_COLUMN_TITLE_PATTERN
}
